import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import loadable from '@loadable/component';
import { useElevate } from './ElevateContext';
import { useTracking } from '../../utils/tracking/useTracking';
import { useHistory, useLocation } from 'react-router';
import { Helmet } from 'react-helmet-async';
import qs from 'qs';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { getLanguageFromCulture } from '@jetshop/core/ChannelHandler/channelUtils';
import { ElevateGrid } from './ElevateGrid';
import { SubsOrSiblings } from '../CategoryPage/SubsOrSiblings';
import CustomerClub from '../Pages/CustomerClub';
import { BrandsPage } from '../BrandsPage/BrandsPage';
import CategoryHeader from '../CategoryPage/CategoryHeader';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import { HtmlBlock } from '../StartPage/ContentRendererComponents/HtmlBlock';
import { useProductVisibilityTracking } from '../../utils/tracking/useProductVisibilityTracking';
import { PAGE_TYPES } from '../../utils/tracking';
import LoadingPage from '../LoadingPage';

const ACADEMY_PATH = '/hooks-academy';

const AcademyPage = loadable(() => import('../Academy/AcademyPage'), {
  fallback: LoadingPage
});

const SingleAcademy = loadable(() => import('../Academy/SingleAcademy'), {
  fallback: LoadingPage
});

/** Overrides Jetshop Head canonical when ?page=1 (page 1 has no page param). */
const CategoryPageCanonical = ({ route }) => {
  const { search } = useLocation();
  const { selectedChannel } = useContext(ChannelContext);

  const { page: pageParam } = qs.parse(search, { ignoreQueryPrefix: true });
  const isPageOne = pageParam !== undefined && Number(pageParam) === 1;

  if (!isPageOne || !route?.canonicalPath) {
    return null;
  }

  let languageBasename = '';
  if (!selectedChannel.language.isDefault) {
    languageBasename = `/${getLanguageFromCulture(
      selectedChannel.language.culture
    )}`;
  }

  const href = `${selectedChannel.url}${languageBasename}${route.canonicalPath}`;

  return (
    <Helmet>
      <link rel="canonical" href={href} />
    </Helmet>
  );
};

const getSubsOrSiblings = category => {
  const siblings = category?.parent?.subcategories;
  const subs = category?.subcategories;
  return subs?.length > 0 ? subs : siblings;
};

const getTopAndBottomCategories = category => {
  const categories = getSubsOrSiblings(category);
  const startsWithExcludation = ['-'];
  const endsWithExcludation = ['*auto*'];

  const topCategories = [];
  const bottomCategories = [];

  (categories || []).forEach(item => {
    if (!item?.name) return;
    const startsWith = startsWithExcludation.some(prefix =>
      item.name.startsWith(prefix)
    );
    const endsWith = endsWithExcludation.some(suffix =>
      item.name.endsWith(suffix)
    );
    if (startsWith || endsWith) {
      bottomCategories.push(item);
    } else {
      topCategories.push(item);
    }
  });

  return { topCategories, bottomCategories };
};

const ElevateCategoryGrid = ({ category, result }) => {
  const history = useHistory();
  const location = useLocation();
  const {
    getLandingPageData,
    queryStringToObject,
    notification,
    NOTIFICATION_TYPES
  } = useElevate();

  const [categoryPageState, setCategoryPageState] = useState({
    productGroups: null,
    totalHits: 0,
    facets: [],
    sortOrder: {},
    listName: ''
  });

  // Do not show the subs or siblings on the activity page
  const isActivityPage = category?.primaryRoute?.path?.startsWith('/activity');

  const { trackSelectItem, trackPageView } = useTracking();
  const { trackProductVisibility, resetTracking } =
    useProductVisibilityTracking({
      listName: category?.name || categoryPageState?.listName || 'Product Grid',
      listId: 'category',
      debounceMs: 300,
      threshold: 0.5,
      routeParents: result.data?.route?.parents
    });

  const pageReference = location.pathname;
  const hashId = location.hash?.replace(/^#/, '') || '';

  const headerItems = [];
  const bottomItems = [];

  if (category?.data?.items?.length > 0) {
    category.data.items.forEach(item => {
      item?.type === 'HtmlBlock' &&
      item?.properties?.some(p => p?.name === 'asSEO' && p?.value)
        ? bottomItems.push(item)
        : headerItems.push(item);
    });
  }

  // New category path → top (do not run when hash is only cleared by pagination)
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [pageReference]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hash entry / SEO block ready → scroll to anchor
  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !hashId) return;

    const el = document.getElementById(hashId);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, [pageReference, hashId, bottomItems.length]);

  const updatePageData = response => {
    setCategoryPageState({
      productGroups: response?.primaryList?.productGroups ?? [],
      totalHits: response?.primaryList?.totalHits ?? 0,
      facets: response?.primaryList?.facets ?? [],
      sortOrder: response?.primaryList?.sort ?? {},
      listName: response?.seo?.pageHeading ?? ''
    });
  };

  useLayoutEffect(() => {
    resetTracking();
  }, [
    categoryPageState.productGroups,
    result?.data?.route?.parents,
    resetTracking
  ]);

  useEffect(() => {
    if (!category) return;

    trackPageView({
      pageType: PAGE_TYPES.CATEGORY,
      pagePath:
        category?.primaryRoute?.path ||
        (typeof window !== 'undefined' ? window.location.pathname : '/'),
      pageTitle:
        category.name || (typeof document !== 'undefined' ? document.title : '')
    });
  }, [category, trackPageView]);

  useEffect(() => {
    if (!pageReference) return;

    let cancelled = false;
    const params = queryStringToObject(history.location.search);
    const fetchDataWithFacets = async () => {
      const isPreview = pageReference.startsWith('/preview');

      const response = await getLandingPageData({
        pageReference: isPreview
          ? category?.primaryRoute?.canonicalPath
          : pageReference,
        ...params
      });
      if (!cancelled && response) {
        updatePageData(response);
      }
    };

    fetchDataWithFacets();
    return () => {
      cancelled = true;
    };
  }, [
    history.location.search,
    pageReference,
    getLandingPageData,
    queryStringToObject
  ]);

  const { topCategories, bottomCategories } =
    getTopAndBottomCategories(category);

  return (
    <div>
      <CategoryPageCanonical route={result?.data?.route} />
      <CategoryHeader
        category={{
          ...category,
          data: { ...category?.data, items: headerItems }
        }}
        parents={result?.data?.route?.parents}
        breadcrumbs={result?.data?.route?.breadcrumbs}
      />
      {isActivityPage ? null : (
        <SubsOrSiblings subsOrSiblings={topCategories} />
      )}
      <ElevateGrid
        pageData={categoryPageState}
        clickCallback={(product, index) => {
          notification(NOTIFICATION_TYPES.click, product?.ticket);
          trackSelectItem({
            product,
            listName: category?.name || categoryPageState?.listName,
            index: index ?? 0
          });
        }}
        inViewCallback={trackProductVisibility}
      />
      {bottomCategories && bottomCategories.length > 0 && (
        <>
          {isActivityPage ? null : (
            <SubsOrSiblings subsOrSiblings={bottomCategories} />
          )}
          <div style={{ height: '20px' }}></div>
        </>
      )}

      {bottomItems.length > 0 && (
        <section
          id="category-seo-content"
          aria-label="Additional category information"
          style={{ marginTop: '10px', marginBottom: '60px' }}
        >
          <ContentRenderer
            items={bottomItems}
            components={{ HTMLBLOCK: HtmlBlock }}
          />
        </section>
      )}
    </div>
  );
};

const ElevateCategoryPage = ({ category, result }) => {
  const location = useLocation();
  const { pathname } = location;

  if (pathname === ACADEMY_PATH || pathname.startsWith(`${ACADEMY_PATH}/`)) {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 6) {
      return <SingleAcademy location={location} />;
    }
    return <AcademyPage location={location} match={{ path: ACADEMY_PATH }} />;
  }

  if (category?.id === 216) {
    return <CustomerClub category={category} />;
  }

  if (category?.id === 540) {
    return <BrandsPage category={category} />;
  }

  return <ElevateCategoryGrid category={category} result={result} />;
};

export default ElevateCategoryPage;
